import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPatch, apiDelete, formatDateTime } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, Check, X, Trash2, User as UserIcon, Crown, Sparkles, Hourglass, Loader2, Search, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import UserDetailModal from "@/components/admin/UserDetailModal";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending:  "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  approved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  rejected: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const TIER_BADGE = {
  basic:   "bg-muted text-muted-foreground border-border",
  trial:   "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  premium: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detailUser, setDetailUser] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // {user, mode: 'reject'|'suspend'} — drives reason dialog
  const [reasonTarget, setReasonTarget] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonBusy, setReasonBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet("/admin/users")
      .then(setUsers)
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load users"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    let out = users;
    if (statusFilter !== "all") out = out.filter((u) => u.status === statusFilter);
    if (tierFilter !== "all") out = out.filter((u) => u.tier === tierFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((u) =>
        (u.full_name || "").toLowerCase().includes(q)
        || (u.first_name || "").toLowerCase().includes(q)
        || (u.last_name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [users, statusFilter, tierFilter, search]);

  const counts = useMemo(
    () => ({
      total: users.length,
      pending: users.filter((u) => u.status === "pending").length,
      approved: users.filter((u) => u.status === "approved").length,
      rejected: users.filter((u) => u.status === "rejected").length,
    }),
    [users]
  );

  const approve = async (u) => {
    try {
      await apiPost(`/admin/users/${u.user_id}/approve`);
      toast.success(`Approved ${u.email} \u2014 14-day Premium trial activated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const openReasonDialog = (u, mode) => {
    setReasonTarget({ user: u, mode });
    setReasonText("");
  };
  const submitReason = async () => {
    if (!reasonTarget) return;
    const { user: u, mode } = reasonTarget;
    const isSuspension = mode === "suspend";
    if (isSuspension && !reasonText.trim()) {
      toast.error("A reason is required when suspending an account");
      return;
    }
    setReasonBusy(true);
    try {
      await apiPost(`/admin/users/${u.user_id}/reject`, {
        is_suspension: isSuspension,
        reason: reasonText.trim(),
      });
      toast.success(isSuspension ? `Suspended ${u.email}` : `Rejected ${u.email}`);
      setReasonTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setReasonBusy(false);
    }
  };
  const reactivate = async (u) => {
    try {
      await apiPost(`/admin/users/${u.user_id}/reactivate`);
      toast.success(`Reactivated ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed"); }
  };
  const setRole = async (u, role) => {
    try {
      await apiPatch(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`Role updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Role update failed"); }
  };
  const setTier = async (u, tier) => {
    try {
      await apiPatch(`/admin/users/${u.user_id}/tier`, { tier });
      toast.success(`Tier updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tier update failed"); }
  };
  const removeUser = async () => {
    if (!deleting) return;
    try {
      await apiDelete(`/admin/users/${deleting.user_id}`);
      toast.success("User deleted");
      setDeleting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">User Management</h2>
          <p className="text-xs text-muted-foreground">Approve registrations, manage roles and subscription tiers.</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <CountPill label="Total" value={counts.total} />
          <CountPill label="Pending" value={counts.pending} tone="warning" />
          <CountPill label="Approved" value={counts.approved} tone="success" />
          <CountPill label="Rejected" value={counts.rejected} tone="destructive" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9 h-9"
            data-testid="admin-users-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="admin-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="admin-tier-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {users.length} users
        </div>
      </div>

      <Card className="border bg-card overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-card border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">User</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">Tier</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">Joined</th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">No users match.</td></tr>
                )}
                {filtered.map((u) => (
                  <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${STATUS_BADGE[u.status] || ""} capitalize text-[10px]`}>
                        {u.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="w-[100px] h-8 text-xs" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">user</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.tier} onValueChange={(v) => setTier(u, v)}>
                        <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`tier-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">basic</SelectItem>
                          <SelectItem value="trial">trial</SelectItem>
                          <SelectItem value="premium">premium</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="sr-only"><Sparkles /><Crown /><Hourglass /></span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {formatDateTime(u.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDetailUser(u)} data-testid={`view-${u.user_id}`} aria-label="View details">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {u.status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => approve(u)} data-testid={`approve-${u.user_id}`}>
                              <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> Approve
                            </Button>
                            <Button size="sm" variant="ghost" className="gap-1.5 h-8" onClick={() => openReasonDialog(u, "reject")} data-testid={`reject-${u.user_id}`}>
                              <X className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" /> Reject
                            </Button>
                          </>
                        )}
                        {u.status === "approved" && (
                          <Button size="sm" variant="ghost" className="gap-1.5 h-8" onClick={() => openReasonDialog(u, "suspend")} data-testid={`suspend-${u.user_id}`}>
                            <X className="h-3.5 w-3.5" /> Suspend
                          </Button>
                        )}
                        {u.status === "rejected" && (
                          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => reactivate(u)} data-testid={`reactivate-${u.user_id}`}>
                            <Check className="h-3.5 w-3.5" /> Reactivate
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-[hsl(var(--destructive))]" onClick={() => setDeleting(u)} data-testid={`delete-${u.user_id}`} aria-label="Delete user">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user {deleting?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the user's account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeUser} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!reasonTarget} onOpenChange={(o) => !o && setReasonTarget(null)}>
        <DialogContent
          className="sm:max-w-[460px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          data-testid="reason-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {reasonTarget?.mode === "suspend" ? "Suspend account" : "Reject registration"}
            </DialogTitle>
            <DialogDescription>
              {reasonTarget?.mode === "suspend" ? (
                <>
                  Suspending <strong>{reasonTarget?.user?.email}</strong>. Provide a reason — this is required and will be included in the email sent to the user.
                </>
              ) : (
                <>
                  Rejecting <strong>{reasonTarget?.user?.email}</strong>. A reason is optional. If provided it will be included in the email sent to the user.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium" htmlFor="reason-text">
              Reason{reasonTarget?.mode === "suspend" ? " (required)" : " (optional)"}
            </label>
            <Textarea
              id="reason-text"
              rows={4}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder={
                reasonTarget?.mode === "suspend"
                  ? "e.g. Multiple terms-of-service violations"
                  : "e.g. Could not verify identity"
              }
              data-testid="reason-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonTarget(null)} disabled={reasonBusy} data-testid="reason-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitReason}
              disabled={reasonBusy || (reasonTarget?.mode === "suspend" && !reasonText.trim())}
              className={
                reasonTarget?.mode === "suspend"
                  ? "bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white gap-2"
                  : "gap-2"
              }
              data-testid="reason-confirm"
            >
              {reasonBusy && <Loader2 className="h-4 w-4 animate-spin" />}
              {reasonTarget?.mode === "suspend" ? "Suspend account" : "Reject registration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserDetailModal
        open={!!detailUser}
        user={detailUser}
        onClose={() => setDetailUser(null)}
        onUserUpdated={(updated) => {
          setDetailUser(updated);
          setUsers((prev) => prev.map((x) => (x.user_id === updated.user_id ? updated : x)));
        }}
      />
    </div>
  );
};

const CountPill = ({ label, value, tone }) => {
  const cls = {
    success: "text-[hsl(var(--success))] border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10",
    warning: "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10",
    destructive: "text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/10",
  }[tone] || "bg-background border";
  return (
    <div className={`hidden md:inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${cls}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular text-foreground">{value}</span>
    </div>
  );
};

export default AdminUsersPage;
